import { NgClass } from '@angular/common'
import { Component, input, output } from '@angular/core'

type EmptyStateMode = 'all' | 'filtered' | 'search' | 'all-archived'

@Component({
  selector: 'app-events-timeline-empty-state',
  standalone: true,
  imports: [NgClass],
  templateUrl: './events-timeline-empty-state.component.html',
  styleUrl: './events-timeline-empty-state.component.scss'
})
export class EventsTimelineEmptyStateComponent {
  public readonly mode = input<EmptyStateMode>('all')
  public readonly filterLabel = input('events')
  public readonly imageSrc = input('')
  public readonly heading = input('')
  public readonly showCreateButton = input(false)
  public readonly canCreate = input(true)
  public readonly createButtonLabel = input('Create first event')

  public readonly createClicked = output<void>()

  /**
   * Resolve heading text by empty-state mode.
   */
  public getHeading(): string {
    const customHeading = this.heading().trim()
    if (customHeading) return customHeading
    if (this.mode() === 'all-archived') return 'No events available'
    if (this.mode() === 'search') return 'No events found.'
    if (this.mode() === 'filtered') return `You don’t have any ${this._formatFilterLabel(this.filterLabel())} events yet.`
    return 'No events yet!'
  }

  /**
   * Resolve subheading for all-events and all-archived empty states.
   */
  public getSubheading(): string {
    if (this.mode() === 'all-archived') return 'Your assigned events have been archived. Access will be restored when they are unarchived.'
    if (this.heading().trim()) return ''
    if (this.mode() !== 'all') return ''
    return 'Start by creating your first event to plan, manage, and track with ease.'
  }

  /**
   * Archived-all copy stays visible even when the user cannot create events.
   */
  public showSubheading(): boolean {
    if (!this.getSubheading()) return false
    if (this.mode() === 'all-archived') return true
    return this.canCreate()
  }

  /**
   * Emit create-event action.
   */
  public onCreateClicked(): void {
    if (!this.canCreate()) return
    this.createClicked.emit()
  }

  public getImageSrc(): string {
    const customImage = this.imageSrc().trim()
    if (customImage) return customImage
    if (this.mode() === 'all-archived') return 'assets/images/v2/all-allowed-events-archived.png'
    return this.mode() === 'all' ? 'assets/images/v2/noEvents.svg' : 'assets/images/v2/noEvents2.svg'
  }

  private _formatFilterLabel(label: string): string {
    const trimmedLabel = label.trim()
    if (!trimmedLabel) return 'events'
    if (trimmedLabel.toLowerCase() === 'past') return 'Finished'
    return `${trimmedLabel.charAt(0).toUpperCase()}${trimmedLabel.slice(1)}`
  }
}
